import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

// Import translation files
import enTranslations from '../locales/en.json';
import plTranslations from '../locales/pl.json';

// Language detection with localStorage priority
const getInitialLanguage = (): string => {
  try {
    // First priority: Check localStorage for saved language preference
    const savedLanguage = localStorage.getItem('geomarkup-language');
    if (savedLanguage && ['en', 'pl'].includes(savedLanguage)) {
      return savedLanguage;
    }

    // Second priority: Check browser language preference
    const browserLanguage = navigator.language.toLowerCase();

    // Handle Polish language variations
    if (browserLanguage.startsWith('pl')) {
      return 'pl';
    }

    // Handle English language variations
    if (browserLanguage.startsWith('en')) {
      return 'en';
    }

    // Default fallback to English
    return 'en';
  } catch (error) {
    console.warn('Error detecting language preference, falling back to English:', error);
    return 'en';
  }
};

// Save language preference to localStorage
const saveLanguagePreference = (language: string): void => {
  try {
    localStorage.setItem('geomarkup-language', language);
  } catch (error) {
    console.warn('Failed to save language preference to localStorage:', error);
  }
};

// Configure i18next
i18n
  .use(initReactI18next) // Pass i18n instance to react-i18next
  .init({
    // Language resources
    resources: {
      en: {
        translation: enTranslations
      },
      pl: {
        translation: plTranslations
      }
    },

    // Language configuration
    lng: getInitialLanguage(), // Initial language
    fallbackLng: 'en', // English as fallback language
    supportedLngs: ['en', 'pl'], // Supported languages

    // Debug configuration (disabled in production)
    debug: import.meta.env.DEV,

    // Interpolation configuration
    interpolation: {
      escapeValue: false, // React already does XSS protection
    },

    // React-specific configuration
    react: {
      useSuspense: false, // Disable Suspense for better React 19 compatibility
      transSupportBasicHtmlNodes: true, // Allow basic HTML nodes in translations
      transKeepBasicHtmlNodesFor: ['br', 'strong', 'i', 'em'], // Allowed HTML tags
    },

    // Key separator and namespace separator
    keySeparator: '.', // Use dots for nested keys: 'common.save'
    nsSeparator: false, // Disable namespace separator to avoid conflicts

    // Other configuration options
    returnEmptyString: false, // Return key instead of empty string for missing translations
    returnNull: false, // Return key instead of null for missing translations

    // Load configuration
    load: 'languageOnly', // Load only 'en' instead of 'en-US'
    preload: ['en', 'pl'], // Preload both supported languages

    // Cache configuration
    cache: {
      enabled: true
    }
  });

// Handle language change events
i18n.on('languageChanged', (language: string) => {
  // Save the new language preference to localStorage
  saveLanguagePreference(language);

  // Update document language attribute for accessibility
  document.documentElement.lang = language;

  // Update Mantine DatesProvider locale if needed
  const mantineDateLocale = language === 'pl' ? 'pl' : 'en';
  document.documentElement.setAttribute('data-mantine-locale', mantineDateLocale);
});

// Set initial document language
document.documentElement.lang = i18n.language;

export default i18n;

// Export utility functions for language management
export const changeLanguage = async (language: string): Promise<void> => {
  await i18n.changeLanguage(language);
};

export const getCurrentLanguage = (): string => {
  return i18n.language;
};

export const getSupportedLanguages = (): string[] => {
  return ['en', 'pl'];
};

export const getLanguageDisplayName = (language: string): string => {
  const displayNames: Record<string, string> = {
    en: 'English',
    pl: 'Polski'
  };
  return displayNames[language] || language;
};